import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, getSecondaryAuth } from "../config/config-firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import API_BASE from "../config/api";
import BottomTabBar from "../components/BottomTabBar";
import useMerchantStores, { getStoreId, isDefaultStoreRow } from "../hooks/useMerchantStores";
import { countryCodes } from "../constants/countryCodes";
import {
  Users,
  Pencil,
  Trash2,
  Check,
  X,
  Phone,
  Mail,
  ShieldCheck,
} from "lucide-react";

const RECAPTCHA_CONTAINER_ID = "recaptcha-container-team-member";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Digits-only comparison so "+1 555-123-4567" and "15551234567" match.
const normalizePhoneDigits = (value) => String(value || "").replace(/\D/g, "");

const emptyAddForm = {
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  countryCode: "+1",
  mobile: "",
  email: "",
  storeId: "",
};

const emptyEditForm = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "+1",
  mobile: "",
};

// Given a full phone number like "+919633188988", find the best-matching
// dial code from countryCodes so we can split it into {countryCode, mobile}
// for pre-filling the edit form.
function splitPhoneNumber(fullPhone) {
  const phone = fullPhone || "";
  const match = countryCodes
    .map((c) => c.dial_code)
    .filter((code) => phone.startsWith(code))
    .sort((a, b) => b.length - a.length)[0];

  if (!match) {
    return { countryCode: "+1", mobile: phone.replace(/^\+/, "") };
  }
  return { countryCode: match, mobile: phone.slice(match.length) };
}

// account_user only stores a combined `name` column ("John Smith") — there's
// no separate first/last name field in the DB — so split it for pre-filling
// the edit form when the row itself has no explicit firstName/lastName.
function splitFullName(fullName) {
  const trimmed = (fullName || "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
}

export default function TeamMembers() {
  const navigate = useNavigate();

  const [uid, setUid] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [actionError, setActionError] = useState("");
  const [addFieldErrors, setAddFieldErrors] = useState({});

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [saving, setSaving] = useState(false);

  // OTP verification for the NEW team member's own mobile number — required
  // so the Create Team Member call carries their own Firebase ID token
  // instead of the merchant's. otpStatus: "idle" | "sent" | "verified".
  const [otpStatus, setOtpStatus] = useState("idle");
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [teamMemberIdToken, setTeamMemberIdToken] = useState(null);
  const confirmationResultRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const { numericUserId, stores, loadingStores, isTeamMember, isTeamMemberActive } = useMerchantStores(uid);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      const resolvedUid = currentUser?.uid || localStorage.getItem("customer_id");
      setUid(resolvedUid || null);
    });
    return () => unsubscribe();
  }, []);

  // Team members don't get a Team Members page — redirect silently (no
  // error messaging, as if the route doesn't exist) once we know the role.
  // A *disabled* team member is redirected to /login by the hook itself
  // (it owns that navigation), so this only handles active members.
  useEffect(() => {
    if (!loadingStores && isTeamMember && isTeamMemberActive) {
      navigate("/user-settings", { replace: true });
    }
  }, [loadingStores, isTeamMember, isTeamMemberActive, navigate]);

  // Tear down the invisible reCAPTCHA widget if this screen unmounts
  // mid-flow (e.g. the merchant navigates away before finishing OTP verify).
  useEffect(() => {
    return () => {
      try {
        recaptchaVerifierRef.current?.clear?.();
      } catch (err) {
        console.error("Error clearing recaptcha on unmount:", err);
      }
    };
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    if (!numericUserId) {
      setTeamMembers([]);
      setLoadingMembers(false);
      return;
    }
    setLoadingMembers(true);
    try {
      const res = await fetch(`${API_BASE}/getMerchantTeamMemberDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: Number(numericUserId) }),
      });
      const json = await res.json().catch(() => ({}));
      const rows = Array.isArray(json?.data) ? json.data : [];
      setTeamMembers(rows);
    } catch (err) {
      console.error("Failed to fetch team members:", err);
      setActionError("Could not load team members.");
      setTeamMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, [numericUserId]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  if (!loadingStores && isTeamMember && isTeamMemberActive) {
    return null;
  }

  const defaultStoreId = (() => {
    const defaultRow = stores.find(isDefaultStoreRow) || stores[0];
    return defaultRow ? getStoreId(defaultRow) : null;
  })();

  // Tears down the invisible reCAPTCHA widget so it doesn't hold a stale
  // grecaptcha widget id across separate "Add team member" sessions.
  const teardownRecaptcha = () => {
    try {
      recaptchaVerifierRef.current?.clear?.();
    } catch (err) {
      console.error("Error clearing recaptcha:", err);
    }
    recaptchaVerifierRef.current = null;
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (container) container.innerHTML = "";
  };

  const resetOtpState = () => {
    setOtpStatus("idle");
    setOtpCode("");
    setOtpError("");
    setTeamMemberIdToken(null);
    confirmationResultRef.current = null;
    teardownRecaptcha();
  };

  const openAddModal = () => {
    // Pre-select the merchant's default store — still changeable in the form.
    setAddForm({ ...emptyAddForm, storeId: defaultStoreId ? String(defaultStoreId) : "" });
    setActionError("");
    setAddFieldErrors({});
    resetOtpState();
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (saving) return;
    setShowAddModal(false);
    resetOtpState();
  };

  // Mobile number is what gets OTP-verified — if it changes after a
  // successful verification, that verification no longer applies to the
  // (new) number, so drop it and make the merchant re-verify.
  const updateAddFormMobileField = (field, value) => {
    setAddForm((p) => ({ ...p, [field]: value }));
    if (otpStatus !== "idle") {
      resetOtpState();
    }
  };

  const handleSendOtp = async () => {
    const mobileDigits = addForm.mobile.trim();
    if (!mobileDigits) {
      setOtpError("Enter the team member's mobile number first.");
      return;
    }

    // Catch the common case instantly, without a round trip: this
    // merchant's own team-member list is already loaded client-side. Check
    // this before spending an OTP send on a number that can't be used.
    const fullMobileDigits = normalizePhoneDigits(`${addForm.countryCode}${mobileDigits}`);
    const mobileAlreadyUsed = teamMembers.some(
      (m) => normalizePhoneDigits(m.phoneNumber || m.mobile) === fullMobileDigits
    );
    if (mobileAlreadyUsed) {
      setOtpError("This mobile number is already linked to another team member.");
      return;
    }

    setSendingOtp(true);
    setOtpError("");
    try {
      const secondaryAuth = getSecondaryAuth();

      if (!recaptchaVerifierRef.current) {
        const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
        if (!container) throw new Error("Recaptcha container not found.");
        recaptchaVerifierRef.current = new RecaptchaVerifier(secondaryAuth, container, {
          size: "invisible",
          callback: () => {},
        });
      }

      const fullMobile = `${addForm.countryCode}${mobileDigits}`;
      const result = await signInWithPhoneNumber(
        secondaryAuth,
        fullMobile,
        recaptchaVerifierRef.current,
      );
      confirmationResultRef.current = result;
      setOtpStatus("sent");
    } catch (err) {
      console.error("Error sending OTP:", err);
      setOtpError(err.message || "Failed to send OTP. Please try again.");
      teardownRecaptcha();
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.trim();
    if (!code || !confirmationResultRef.current) return;

    setVerifyingOtp(true);
    setOtpError("");
    try {
      const result = await confirmationResultRef.current.confirm(code);
      const idToken = await result.user.getIdToken();
      setTeamMemberIdToken(idToken);
      setOtpStatus("verified");
      // Free up the isolated auth session now that we have the token —
      // keeps it clean for the next team member added in this sitting.
      await getSecondaryAuth().signOut();
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setOtpError(err.message || "Invalid code. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const openEditModal = (member) => {
    const fullPhone = member.phoneNumber || member.mobile || "";
    const { countryCode, mobile } = splitPhoneNumber(fullPhone);
    const nameFromFull = splitFullName(member.name);
    setEditMember(member);
    setEditForm({
      firstName: member.firstName || member.first_name || nameFromFull.firstName,
      lastName: member.lastName || member.last_name || nameFromFull.lastName,
      email: member.email || "",
      countryCode,
      mobile,
    });
    setActionError("");
  };

  const handleAddMember = async () => {
    const firstName = addForm.firstName.trim();
    const lastName = addForm.lastName.trim();
    const username = addForm.username.trim();
    const password = addForm.password;
    const mobileDigits = addForm.mobile.trim();
    const email = addForm.email.trim();

    if (!firstName || !lastName || !username || !password || !mobileDigits || !email || !addForm.storeId) return;
    if (!EMAIL_PATTERN.test(email)) {
      setActionError("Enter a valid email address.");
      return;
    }

    // Catch the common case instantly, without a round trip: this
    // merchant's own team-member list is already loaded client-side.
    const emailAlreadyUsed = teamMembers.some(
      (m) => (m.email || "").trim().toLowerCase() === email.toLowerCase()
    );
    if (emailAlreadyUsed) {
      setAddFieldErrors({ email: "This email already exists." });
      return;
    }

    // Re-check the mobile too — handleSendOtp already checked this before
    // the OTP round trip, but re-verify here in case another team member
    // using the same mobile was added elsewhere between then and now.
    const fullMobileDigits = normalizePhoneDigits(`${addForm.countryCode}${mobileDigits}`);
    const mobileAlreadyUsed = teamMembers.some(
      (m) => normalizePhoneDigits(m.phoneNumber || m.mobile) === fullMobileDigits
    );
    if (mobileAlreadyUsed) {
      setAddFieldErrors({ mobile: "This mobile number is already linked to another team member." });
      return;
    }

    if (!numericUserId || stores.length === 0) {
      setActionError("Still loading your store details — try again in a moment.");
      return;
    }

    if (!addForm.storeId) {
      setActionError("Select a store to assign this team member to.");
      return;
    }

    if (otpStatus !== "verified" || !teamMemberIdToken) {
      setActionError("Please verify the team member's mobile number via OTP first.");
      return;
    }

    setSaving(true);
    setActionError("");
    setAddFieldErrors({});
    try {
      const mobile = `${addForm.countryCode}${mobileDigits}`;

      const res = await fetch(`${API_BASE}/saveTeamMember`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeId: Number(addForm.storeId),
          token: teamMemberIdToken,
          firstName,
          lastName,
          email,
          phoneNumber: mobile,
          username,
          password,
          userId: Number(numericUserId),
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        if (Array.isArray(json.errors) && json.errors.length > 0) {
          const fieldErrors = {};
          json.errors.forEach((e) => {
            if (e?.field && e?.message) fieldErrors[e.field] = e.message;
          });
          setAddFieldErrors(fieldErrors);
          return;
        }

        // The backend's actual failure message is a fixed generic string
        // ("Saving teammember details failed") with the real MySQL error
        // (e.g. "Duplicate entry 'x@y.com' for key 'account_user.email'")
        // sent separately in `error` — inspect both so a DB-level duplicate
        // (not just a duplicate among this merchant's own team, already
        // caught client-side above) still surfaces a proper message.
        const lowerMessage = `${json.message || ""} ${json.error || ""}`.toLowerCase();
        const fallbackErrors = {};

        if (lowerMessage.includes("email")) {
          fallbackErrors.email = "This email already exists.";
        }

        if (
          lowerMessage.includes("phone") ||
          lowerMessage.includes("mobile") ||
          lowerMessage.includes("firebase") ||
          lowerMessage.includes("token") ||
          lowerMessage.includes("already-exists") ||
          (lowerMessage.includes("already exists") && !lowerMessage.includes("email"))
        ) {
          fallbackErrors.mobile = "This mobile number is already linked to another team member.";
        }

        if (Object.keys(fallbackErrors).length > 0) {
          setAddFieldErrors(fallbackErrors);
          return;
        }

        throw new Error(json.message || "Failed to add team member.");
      }

      setShowAddModal(false);
      setAddForm(emptyAddForm);
      resetOtpState();
      await fetchTeamMembers();
    } catch (err) {
      console.error("Error adding team member:", err);
      setActionError(err.message || "Failed to add team member.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const firstName = editForm.firstName.trim();
    const lastName = editForm.lastName.trim();
    const email = editForm.email.trim();
    const mobileDigits = editForm.mobile.trim();
    if (!firstName || !lastName || !email || !mobileDigits || !editMember) return;
    if (!EMAIL_PATTERN.test(email)) {
      setActionError("Enter a valid email address.");
      return;
    }

    setSavingEdit(true);
    setActionError("");
    try {
      const phoneNumber = `${editForm.countryCode}${mobileDigits}`;

      const res = await fetch(`${API_BASE}/updateTeamMemberDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          teamMemberId: editMember.id,
          firstName,
          lastName,
          email,
          phoneNumber,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to update team member.");
      }

      setEditMember(null);
      await fetchTeamMembers();
    } catch (err) {
      console.error("Error updating team member:", err);
      setActionError(err.message || "Failed to update team member.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleAccess = async (member) => {
    const nextStatus = !member.is_teammember_active;
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/changeTeamMemberStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ teamMemberId: member.id, status: nextStatus }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to update access.");
      }

      await fetchTeamMembers();
    } catch (err) {
      console.error("Error toggling team member access:", err);
      setActionError(err.message || "Failed to update access.");
    }
  };

  const handleDeleteMember = async () => {
    if (!deleteTarget) return;
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/deleteTeamMember`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          teamMemberId: deleteTarget.id,
          userId: numericUserId,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to remove team member.");
      }

      setDeleteTarget(null);
      await fetchTeamMembers();
    } catch (err) {
      console.error("Error deleting team member:", err);
      setActionError(err.message || "Failed to remove team member.");
    }
  };

  const screenShell =
    "min-h-[100dvh] bg-white w-full min-w-0 overflow-x-hidden flex flex-col pb-24 sm:pb-28";
  const contentWrap =
    "w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto flex-1 flex flex-col min-w-0";

  return (
    <div className={screenShell}>
      <div className={contentWrap}>
        <div className="px-4 sm:px-5 pt-8 sm:pt-10 pb-4">
          <button
            type="button"
            onClick={() => navigate("/user-settings")}
            className="text-sm text-gray-500 flex items-center gap-1 mb-4 py-1 touch-manipulation"
          >
            ← Back
          </button>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">
                Team Members
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Add users who can log in and access this store with their own mobile number.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/add-team-member', { state: { teamMembers, stores, numericUserId, defaultStoreId } })}
              className="flex items-center gap-1.5 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-full flex-shrink-0 mt-1 touch-manipulation"
            >
              + Add User
            </button>
          </div>
        </div>

        {actionError && (
          <div className="mx-4 sm:mx-5 mb-3 bg-red-50 border border-red-100 text-red-600 text-xs sm:text-sm rounded-xl px-4 py-3">
            {actionError}
          </div>
        )}

        <div className="flex items-center gap-2 px-4 sm:px-5 mb-3">
          <span className="text-sm font-bold text-gray-900">Members</span>
          <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {teamMembers.length}
          </span>
        </div>

        {loadingMembers ? (
          <div className="mx-4 sm:mx-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-sm text-gray-400">
            Loading team members…
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="mx-4 sm:mx-5 bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <Users size={26} className="text-blue-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">
              No team members yet
            </p>
            <p className="text-xs text-gray-400 mb-5 max-w-xs">
              Add a team member so they can log in with their own mobile
              number and access this store.
            </p>
            <button
              type="button"
              onClick={() => navigate('/add-team-member', { state: { teamMembers, stores, numericUserId, defaultStoreId } })}
              className="bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-full touch-manipulation"
            >
              + Add First User
            </button>
          </div>
        ) : (
          <div className="mx-4 sm:mx-5 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {teamMembers.map((member, index) => {
              const displayName =
                member.username ||
                member.name ||
                [member.firstName, member.lastName].filter(Boolean).join(" ") ||
                "?";
              const phoneDisplay = member.phoneNumber || member.mobile || "No phone added";

              return (
                <div
                  key={member.id}
                  className={`px-4 sm:px-5 py-3.5 sm:py-4 ${index !== teamMembers.length - 1
                    ? "border-b border-gray-100"
                    : ""
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${member.is_teammember_active
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-400"
                        }`}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {displayName}
                        </p>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${member.is_teammember_active
                            ? "bg-green-50 text-green-600 border-green-200"
                            : "bg-red-50 text-red-500 border-red-200"
                            }`}
                        >
                          {member.is_teammember_active ? "Active" : "Disabled"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Phone size={10} className="text-gray-400 flex-shrink-0" />
                        <p className="text-xs text-gray-400 truncate">
                          {phoneDisplay}
                        </p>
                      </div>
                      {member.email && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Mail size={10} className="text-gray-400 flex-shrink-0" />
                          <p className="text-xs text-gray-400 truncate">
                            {member.email}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleToggleAccess(member)}
                        className={`relative inline-flex items-center w-10 h-5 rounded-full border-2 transition-all duration-200 focus:outline-none flex-shrink-0 ${member.is_teammember_active
                          ? "bg-blue-600 border-blue-600"
                          : "bg-white border-gray-300"
                          }`}
                        aria-label="Toggle access"
                      >
                        <span
                          className={`inline-block w-3 h-3 bg-white rounded-full shadow-md transform transition-transform duration-200 ${member.is_teammember_active
                            ? "translate-x-5"
                            : "translate-x-0.5 bg-gray-400"
                            }`}
                        />
                      </button>

                      <button
                        type="button"
                        onClick={() => openEditModal(member)}
                        className="border border-gray-200 p-1.5 rounded-lg touch-manipulation"
                        aria-label="Edit member"
                      >
                        <Pencil size={13} className="text-gray-400" />
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeleteTarget(member)}
                        className="bg-red-50 p-1.5 rounded-lg touch-manipulation"
                        aria-label="Delete member"
                      >
                        <Trash2 size={13} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomTabBar />

      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center sm:justify-center p-0 sm:p-8">
          <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl px-5 pt-3 pb-8 shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />

            <div className="flex justify-between items-center mb-5">
              <h2 className="text-base font-bold text-gray-900">Add team member</h2>
              <button
                type="button"
                onClick={closeAddModal}
                className="p-0.5 touch-manipulation"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    First name
                  </label>
                  <input
                    type="text"
                    placeholder="First name"
                    value={addForm.firstName}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, firstName: e.target.value }))
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    Last name
                  </label>
                  <input
                    type="text"
                    placeholder="Last name"
                    value={addForm.lastName}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, lastName: e.target.value }))
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Enter username"
                  value={addForm.username}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, username: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Set a password"
                  value={addForm.password}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, password: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Store
                </label>
                <select
                  value={addForm.storeId}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, storeId: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                >
                  <option value="" disabled>
                    Select a store
                  </option>
                  {stores.map((store) => {
                    const id = getStoreId(store);
                    if (!id) return null;
                    return (
                      <option key={id} value={id}>
                        {store.full_name || store.name || `Store #${id}`}
                      </option>
                    );
                  })}
                </select>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  They'll only be able to log in to this store.
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Mobile number
                </label>
                <div className="flex gap-2">
                  <select
                    value={addForm.countryCode}
                    onChange={(e) => updateAddFormMobileField("countryCode", e.target.value)}
                    disabled={otpStatus !== "idle"}
                    className="border border-gray-200 rounded-xl px-2 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.dial_code}>
                        {c.dial_code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={addForm.mobile}
                    onChange={(e) => updateAddFormMobileField("mobile", e.target.value)}
                    disabled={otpStatus !== "idle"}
                    className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  They'll log in using this number with an OTP, same as you do.
                </p>

                {addFieldErrors.mobile && (
                  <p className="text-[11px] text-red-500 mt-1.5">{addFieldErrors.mobile}</p>
                )}

                {/* OTP verification — required before the member can be created,
                    so the create-team-member request carries their own Firebase
                    identity instead of the merchant's. */}
                <div className="mt-3">
                  {otpStatus === "idle" && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={!addForm.mobile.trim() || sendingOtp}
                      className="w-full border border-blue-200 text-blue-600 text-xs font-semibold py-2.5 rounded-xl disabled:opacity-50 touch-manipulation"
                    >
                      {sendingOtp ? "Sending OTP…" : "Send OTP to verify number"}
                    </button>
                  )}

                  {otpStatus === "sent" && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          inputMode="numeric"
                          placeholder="Enter OTP"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                          className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={!otpCode.trim() || verifyingOtp}
                          className="bg-blue-600 text-white text-xs font-bold px-4 rounded-xl disabled:opacity-50 touch-manipulation"
                        >
                          {verifyingOtp ? "Verifying…" : "Verify"}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={sendingOtp}
                        className="text-[11px] text-blue-600 font-medium touch-manipulation"
                      >
                        {sendingOtp ? "Resending…" : "Resend OTP"}
                      </button>
                    </div>
                  )}

                  {otpStatus === "verified" && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                      <ShieldCheck size={14} />
                      Mobile number verified
                    </div>
                  )}

                  {otpError && (
                    <p className="text-[11px] text-red-500 mt-1.5">{otpError}</p>
                  )}
                </div>

                {/* Invisible reCAPTCHA host for the OTP flow above. Runs on an
                    isolated secondary Firebase Auth instance (see
                    config-firebase.js) so verifying the team member's phone
                    never touches the merchant's own logged-in session. */}
                <div id={RECAPTCHA_CONTAINER_ID} />
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={addForm.email}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, email: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                {addFieldErrors.email && (
                  <p className="text-[11px] text-red-500 mt-1.5">{addFieldErrors.email}</p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddMember}
              disabled={
                !addForm.firstName.trim() ||
                !addForm.lastName.trim() ||
                !addForm.username.trim() ||
                !addForm.password ||
                !addForm.mobile.trim() ||
                !addForm.email.trim() ||
                !addForm.storeId ||
                otpStatus !== "verified" ||
                saving
              }
              className="w-full bg-blue-600 text-white font-bold text-sm py-4 rounded-full mt-6 disabled:opacity-40 flex items-center justify-center gap-2 touch-manipulation"
            >
              <Check size={15} />
              {saving ? "Adding…" : "Add member"}
            </button>
          </div>
        </div>
      )}

      {editMember && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center sm:justify-center p-0 sm:p-8">
          <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl px-5 pt-3 pb-8 shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />

            <div className="flex justify-between items-center mb-5">
              <h2 className="text-base font-bold text-gray-900">Edit member</h2>
              <button
                type="button"
                onClick={() => setEditMember(null)}
                className="p-0.5 touch-manipulation"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    First name
                  </label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, firstName: e.target.value }))
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-gray-600 font-medium mb-1 block">
                    Last name
                  </label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, lastName: e.target.value }))
                    }
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, email: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Mobile number
                </label>
                <div className="flex gap-2">
                  <select
                    value={editForm.countryCode}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, countryCode: e.target.value }))
                    }
                    className="border border-gray-200 rounded-xl px-2 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.dial_code}>
                        {c.dial_code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={editForm.mobile}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, mobile: e.target.value }))
                    }
                    className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={
                !editForm.firstName.trim() ||
                !editForm.lastName.trim() ||
                !editForm.email.trim() ||
                !editForm.mobile.trim() ||
                savingEdit
              }
              className="w-full bg-blue-600 text-white font-bold text-sm py-4 rounded-full mt-6 disabled:opacity-40 flex items-center justify-center gap-2 touch-manipulation"
            >
              <Check size={15} /> {savingEdit ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-xs shadow-2xl">
            <div className="bg-red-50 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">
              Remove member
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to remove{" "}
              <strong className="text-gray-900">
                {deleteTarget.username ||
                  deleteTarget.name ||
                  [deleteTarget.firstName, deleteTarget.lastName].filter(Boolean).join(" ") ||
                  "this member"}
              </strong>
              ? They will lose access to this store immediately.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteMember}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm touch-manipulation"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}